import { Box, Group, Select, Text, Tooltip } from '@mantine/core';
import { useState } from 'react';
import type { Shema } from '../api/odjemalec';
import { barve } from '../tema';
import { vrednost as oblika } from './asistent';

type Kartica = Shema['AsistentKartica'];

const VISINA = 170;
/** Vodoravni graf pokaže toliko vrstic (kot tabela, v istem vrstnem redu). */
const NAJVEC_VRSTIC = 30;

/**
 * Tabela kartice kot stolpčni graf, en niz naenkrat (izbira mere iz {@code graf.mere}), zato brez legende in z eno osjo;
 * negativne vrednosti gredo pod oz. levo od osnovnice. Časovno zaporedje (meseci, četrtletja) ima navpične stolpce po
 * vrsti, ostalo vodoravne v vrstnem redu tabele (razvrstitev določa strežnik). Vrednost ob prehodu z miško, fokusu ali
 * dotiku; tabela je drugi pogled iste kartice.
 */
export function GrafKartice({ kartica }: { kartica: Kartica }) {
  const graf = kartica.graf!;
  const stolpci = kartica.stolpci ?? [];
  const mere = (graf.mere ?? []).map((k) => stolpci.find((c) => c.kljuc === k)).filter((c) => !!c);
  const [kljuc, setKljuc] = useState(mere[0]?.kljuc ?? '');
  const mera = mere.find((m) => m.kljuc === kljuc) ?? mere[0];
  if (!mera) {
    return null;
  }
  const vrstice = (kartica.vrstice ?? []).slice(0, graf.casovno ? undefined : NAJVEC_VRSTIC);
  const tocke = vrstice.map((r) => ({
    naziv: String(r[graf.os ?? ''] ?? ''),
    v: typeof r[mera.kljuc ?? ''] === 'number' ? (r[mera.kljuc ?? ''] as number) : 0,
  }));
  const najvec = Math.max(0, ...tocke.map((t) => t.v));
  const najmanj = Math.min(0, ...tocke.map((t) => t.v));
  const razpon = najvec - najmanj || 1;
  // delež od začetka osi do osnovnice (0)
  const nic = -najmanj / razpon;

  return (
    <Box>
      <Group justify="space-between" align="center" gap="xs" mb={8} wrap="wrap-reverse">
        <Text size="xs" c={barve.besediloSekundarno}>
          največ {oblika(najvec, mera.vrsta)}
          {najmanj < 0 ? ` · najmanj ${oblika(najmanj, mera.vrsta)}` : ''}
        </Text>
        {mere.length > 1 && (
          <Select
            size="xs"
            w={190}
            aria-label="Prikaži v grafu"
            data={mere.map((m) => ({ value: m.kljuc ?? '', label: m.naziv ?? '' }))}
            value={mera.kljuc ?? ''}
            onChange={(v) => v && setKljuc(v)}
            allowDeselect={false}
          />
        )}
      </Group>
      {graf.casovno ? (
        <Navpicni tocke={tocke} razpon={razpon} nic={nic} mera={mera.naziv ?? ''} vrsta={mera.vrsta} />
      ) : (
        <Vodoravni tocke={tocke} razpon={razpon} nic={nic} mera={mera.naziv ?? ''} vrsta={mera.vrsta} />
      )}
      {!graf.casovno && (kartica.vrstice ?? []).length > NAJVEC_VRSTIC && (
        <Text size="xs" c="dimmed" mt={4}>
          V grafu je prvih {NAJVEC_VRSTIC} vrstic, vse so v tabeli.
        </Text>
      )}
    </Box>
  );
}

type Tocke = { naziv: string; v: number }[];
type Lastnosti = { tocke: Tocke; razpon: number; nic: number; mera: string; vrsta?: string };

function Navpicni({ tocke, razpon, nic, mera, vrsta }: Lastnosti) {
  return (
    <>
      <Box role="group" aria-label={mera} style={{ display: 'flex', gap: 2, height: VISINA, position: 'relative' }}>
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${nic * 100}%`,
            borderTop: `1px solid ${barve.obroba}`,
          }}
        />
        {tocke.map((t) => {
          const visina = (Math.abs(t.v) / razpon) * 100;
          const besedilo = `${t.naziv}: ${oblika(t.v, vrsta)}`;
          return (
            <Tooltip key={t.naziv} label={besedilo} withArrow events={{ hover: true, focus: true, touch: true }}>
              <Box
                role="img"
                aria-label={besedilo}
                tabIndex={0}
                style={{ flex: 1, minWidth: 0, position: 'relative', height: '100%' }}
              >
                <Box
                  style={{
                    position: 'absolute',
                    left: '12%',
                    right: '12%',
                    background: barve.primarna,
                    ...(t.v >= 0
                      ? { bottom: `${nic * 100}%`, height: `${visina}%`, borderRadius: '4px 4px 0 0' }
                      : { top: `${(1 - nic) * 100}%`, height: `${visina}%`, borderRadius: '0 0 4px 4px' }),
                    minHeight: t.v !== 0 ? 2 : 0,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box style={{ display: 'flex', gap: 2, marginTop: 6 }} aria-hidden>
        {tocke.map((t) => (
          <Text
            key={t.naziv}
            size="xs"
            ta="center"
            c={barve.besediloSekundarno}
            style={{ flex: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
          >
            {t.naziv}
          </Text>
        ))}
      </Box>
    </>
  );
}

function Vodoravni({ tocke, razpon, nic, mera, vrsta }: Lastnosti) {
  return (
    <Box
      role="group"
      aria-label={mera}
      style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 38%) 1fr', rowGap: 4, columnGap: 8 }}
    >
      {tocke.map((t, i) => {
        const sirina = (Math.abs(t.v) / razpon) * 100;
        const besedilo = `${t.naziv}: ${oblika(t.v, vrsta)}`;
        return (
          <Box key={`${t.naziv}-${i}`} style={{ display: 'contents' }}>
            <Text size="xs" truncate title={t.naziv} c={barve.besedilo}>
              {t.naziv}
            </Text>
            <Tooltip label={besedilo} withArrow events={{ hover: true, focus: true, touch: true }}>
              <Box role="img" aria-label={besedilo} tabIndex={0} style={{ position: 'relative', height: 18 }}>
                <Box
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${nic * 100}%`,
                    borderLeft: `1px solid ${barve.obroba}`,
                  }}
                />
                <Box
                  style={{
                    position: 'absolute',
                    top: 3,
                    bottom: 3,
                    background: barve.primarna,
                    ...(t.v >= 0
                      ? { left: `${nic * 100}%`, width: `${sirina}%`, borderRadius: '0 4px 4px 0' }
                      : { right: `${(1 - nic) * 100}%`, width: `${sirina}%`, borderRadius: '4px 0 0 4px' }),
                    minWidth: t.v !== 0 ? 2 : 0,
                  }}
                />
              </Box>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}
